import { Buffer } from "buffer";

import {
    CLMM_PROGRAM_ID,
    getPdaPersonalPositionAddress,
    LiquidityMathUtil,
    PersonalPositionLayout,
    PoolInfoLayout,
    TickUtil
} from "@raydium-io/raydium-sdk-v2";
import { USDV_MINT } from "@shared/tokens/usdv";
import { PublicKey } from "@solana/web3.js";
import type { YaasLpPositionIdentity } from "@solomon-apps/shared/src/types/yaas";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { ConnectionManager } from "@solomon-labs/solana-connection";

import { isOwnedNft } from "./lpOwnership";
import { requestLpRpc } from "./lpRpc";
import type { LpBalance, LpRpc, ParsedTokenAccountsResponse, RpcAccount, RpcAccountsResponse } from "./lpTypes";

// Anchor account discriminators: sha256("account:<name>")[0..8].
const POSITION_DISCRIMINATOR = [70, 111, 150, 126, 230, 15, 25, 117];
const POOL_DISCRIMINATOR = [247, 237, 227, 245, 215, 195, 222, 70];

export function decodeRaydiumPosition(
    account: RpcAccount | null | undefined
): ReturnType<typeof PersonalPositionLayout.decode> {
    return PersonalPositionLayout.decode(accountBytes(account, POSITION_DISCRIMINATOR));
}

export function decodeRaydiumPool(account: RpcAccount | null | undefined): ReturnType<typeof PoolInfoLayout.decode> {
    return PoolInfoLayout.decode(accountBytes(account, POOL_DISCRIMINATOR));
}

function accountBytes(account: RpcAccount | null | undefined, discriminator: number[]): Buffer {
    if (account?.owner !== CLMM_PROGRAM_ID.toBase58() || account.data[1] !== "base64")
        throw new Error("Raydium account missing or owned by another program");
    const bytes = Buffer.from(account.data[0], "base64");
    if (!discriminator.every((byte, index) => bytes[index] === byte))
        throw new Error("Unexpected Raydium account type");
    return bytes;
}

/** Current USDv principal only: excludes trading fees, rewards and transfer fees on withdrawal. */
export function calculateLpUsdv(
    position: ReturnType<typeof PersonalPositionLayout.decode>,
    pool: ReturnType<typeof PoolInfoLayout.decode>,
    mint: string
): string {
    if (mint !== USDV_MINT) throw new Error("Unsupported qualifying mint");
    const sideA = pool.mintA.toBase58() === mint;
    if (!sideA && pool.mintB.toBase58() !== mint) throw new Error("Pool does not contain USDv");
    if (position.tickLower >= position.tickUpper || pool.sqrtPriceX64.isZero())
        throw new Error("Invalid CLMM price range");
    const { amountA, amountB } = LiquidityMathUtil.getAmountsForLiquidity(
        pool.sqrtPriceX64,
        TickUtil.getSqrtPriceAtTick(position.tickLower),
        TickUtil.getSqrtPriceAtTick(position.tickUpper),
        position.liquidity,
        false
    );
    return new BigDecimal((sideA ? amountA : amountB).toString())
        .divide(BigDecimal.pow10(sideA ? pool.mintDecimalsA : pool.mintDecimalsB))
        .toString();
}

/** Accept a personal-position PDA or NFT mint. Returns null only for confirmed lost ownership. */
export async function fetchRaydiumLpBalance(
    identity: YaasLpPositionIdentity,
    walletAddress: string,
    signal?: AbortSignal,
    rpc: LpRpc = ConnectionManager.getRpcClient(),
    minContextSlot?: number
): Promise<LpBalance | null> {
    const supplied = new PublicKey(identity.positionAddress);
    const derived = getPdaPersonalPositionAddress(CLMM_PROGRAM_ID, supplied).publicKey;
    const response = await requestLpRpc<RpcAccountsResponse>(
        rpc,
        "getMultipleAccounts",
        [
            [supplied.toBase58(), derived.toBase58(), identity.poolAddress],
            { encoding: "base64", commitment: "confirmed", ...(minContextSlot === undefined ? {} : { minContextSlot }) }
        ],
        { signal, timeout: 15_000 }
    );
    const direct = response.value[0]?.owner === CLMM_PROGRAM_ID.toBase58();
    const positionAddress = direct ? supplied : derived;
    const position = decodeRaydiumPosition(response.value[direct ? 0 : 1]);
    if (
        !getPdaPersonalPositionAddress(CLMM_PROGRAM_ID, position.nftMint).publicKey.equals(positionAddress) ||
        (!direct && !position.nftMint.equals(supplied)) ||
        position.poolId.toBase58() !== identity.poolAddress
    )
        throw new Error("LP identity does not match its pool or NFT");
    const pool = decodeRaydiumPool(response.value[2]);
    const ownership = await requestLpRpc<ParsedTokenAccountsResponse>(
        rpc,
        "getTokenAccountsByOwner",
        [
            walletAddress,
            { mint: position.nftMint.toBase58() },
            {
                encoding: "jsonParsed",
                commitment: "confirmed",
                minContextSlot: response.context.slot
            }
        ],
        { signal, timeout: 15_000 }
    );
    if (
        !ownership.value.some(({ account }) => {
            const info = account.data.parsed.info;
            return isOwnedNft(info, walletAddress) && info.mint === position.nftMint.toBase58();
        })
    )
        return null; // A transferred NFT is an ownership result, not a failed RPC read.
    return {
        amount: calculateLpUsdv(position, pool, identity.observedAssetMint),
        slot: response.context.slot
    };
}
